import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "./icons";

export const HeroCarousel = ({ images, alt, intervalMs = 3500, fallback }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [failed, setFailed] = useState(() => ({}));

  const slides = useMemo(() => {
    const srcList = Array.isArray(images) ? images.filter(Boolean) : [];
    const uniq = [];
    for (const src of srcList) {
      if (failed[src]) continue;
      if (!uniq.includes(src)) uniq.push(src);
    }
    return uniq;
  }, [images, failed]);

  useEffect(() => {
    if (slides.length < 2 || paused) return;
    const id = setInterval(() => {
      setActiveIndex((idx) => (slides.length ? (idx + 1) % slides.length : 0));
    }, intervalMs);
    return () => clearInterval(id);
  }, [slides.length, paused, intervalMs]);

  useEffect(() => {
    if (activeIndex >= slides.length) setActiveIndex(0);
  }, [activeIndex, slides.length]);

  const goTo = useCallback(
    (idx) => {
      if (!slides.length) return;
      const next = ((idx % slides.length) + slides.length) % slides.length;
      setActiveIndex(next);
    },
    [slides.length]
  );

  const markFailed = useCallback((src) => setFailed((prev) => ({ ...prev, [src]: true })), []);

  if (!slides.length) return fallback || null;

  return (
    <div
      className="relative w-full h-[300px] sm:h-[360px]"
      role="region"
      aria-roledescription="carousel"
      aria-label="Galeria de imagens da igreja"
      tabIndex={0}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          goTo(activeIndex - 1);
        }
        if (e.key === "ArrowRight") {
          e.preventDefault();
          goTo(activeIndex + 1);
        }
        if (e.key === " " || e.key === "Spacebar") {
          e.preventDefault();
          setPaused((v) => !v);
        }
      }}
    >
      {slides.map((src, idx) => (
        <img
          key={src}
          src={src}
          alt={alt}
          onError={() => markFailed(src)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${idx === activeIndex ? "opacity-100" : "opacity-0"}`}
          loading={idx === 0 ? "eager" : "lazy"}
          fetchpriority={idx === 0 ? "high" : "low"}
          decoding="async"
          draggable="false"
        />
      ))}
      <div className="sr-only" aria-live="polite">
        Imagem {activeIndex + 1} de {slides.length}
      </div>

      {slides.length > 1 ? (
        <>
          <button
            type="button"
            onClick={() => goTo(activeIndex - 1)}
            aria-label="Anterior"
            className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-slate-900/40 hover:bg-slate-900/60 border border-white/30 backdrop-blur flex items-center justify-center transition"
          >
            <ChevronLeftIcon className="w-5 h-5 text-white" />
          </button>
          <button
            type="button"
            onClick={() => goTo(activeIndex + 1)}
            aria-label="Próxima"
            className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-slate-900/40 hover:bg-slate-900/60 border border-white/30 backdrop-blur flex items-center justify-center transition"
          >
            <ChevronRightIcon className="w-5 h-5 text-white" />
          </button>
          <button
            type="button"
            onClick={() => setPaused((v) => !v)}
            aria-label={paused ? "Retomar rotação" : "Pausar rotação"}
            className="absolute top-3 right-3 px-3 py-1.5 rounded-full bg-slate-900/45 hover:bg-slate-900/65 border border-white/30 text-xs font-semibold text-white transition"
          >
            {paused ? "Retomar" : "Pausar"}
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {slides.map((src, idx) => (
              <button
                key={src}
                type="button"
                onClick={() => goTo(idx)}
                aria-label={`Ir para imagem ${idx + 1}`}
                className={`h-2.5 w-2.5 rounded-full transition ${idx === activeIndex ? "bg-white dark:bg-slate-900" : "bg-white dark:bg-slate-900/50 hover:bg-white dark:hover:bg-slate-900/70"}`}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};
